import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileSignature, Plus, Loader2, Trash2, Printer } from "lucide-react";
import { toast } from "sonner";

export default function Bons() {
  return (
    <div>
      <PageHeader title="Bons d'intervention & de livraison" subtitle="Documents professionnels signables et imprimables" icon={FileSignature} />
      <Tabs defaultValue="intervention">
        <TabsList className="mb-5"><TabsTrigger value="intervention" data-testid="bons-tab-bi">Bons d'intervention</TabsTrigger><TabsTrigger value="delivery" data-testid="bons-tab-bl">Bons de livraison</TabsTrigger></TabsList>
        <TabsContent value="intervention"><BonList endpoint="/intervention-reports" kind="bi" /></TabsContent>
        <TabsContent value="delivery"><BonList endpoint="/delivery-notes" kind="bl" /></TabsContent>
      </Tabs>
    </div>
  );
}

function BonList({ endpoint, kind }) {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    try { setRows((await api.get(endpoint)).data); setClients((await api.get("/clients")).data || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const save = async () => {
    try { await api.post(endpoint, { ...form, client_id: form.client_id || null }); toast.success("Bon créé"); setOpen(false); setForm({}); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`${endpoint}/${id}`); load(); };
  const print = (r) => {
    const w = window.open("", "_blank");
    const name = clients.find((c) => c.id === r.client_id);
    w.document.write(`<html><head><title>${r.number}</title><style>body{font-family:Arial;padding:40px;color:#111}h1{color:#059669}table{width:100%;border-collapse:collapse;margin-top:16px}td,th{border:1px solid #ddd;padding:8px;text-align:left}.sig{margin-top:60px;display:flex;justify-content:space-between}</style></head><body>
      <h1>${kind === "bi" ? "Bon d'intervention" : "Bon de livraison"} ${r.number}</h1>
      <p><b>Date :</b> ${r.date || ""}<br/><b>Client :</b> ${name ? (name.first_name || "") + " " + (name.last_name || "") + " " + (name.company || "") : (r.client_name || "")}</p>
      ${r.address ? `<p><b>Adresse :</b> ${r.address}</p>` : ""}
      ${r.work ? `<p><b>Travaux :</b> ${r.work}</p>` : ""}
      ${r.details ? `<p>${r.details}</p>` : ""}
      ${r.hours != null ? `<p><b>Temps passé :</b> ${r.hours} h</p>` : ""}
      ${r.observations ? `<p><b>Observations :</b> ${r.observations}</p>` : ""}
      <div class="sig"><div>Signature pro :<br/>${r.pro_signature || "____________"}</div><div>Signature client :<br/>${r.client_signature || "____________"}</div></div>
      </body></html>`);
    w.document.close(); w.print();
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  return (
    <div className="space-y-3">
      <Button onClick={() => setOpen(true)} data-testid={`btn-new-${kind}`} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"><Plus className="w-4 h-4 mr-1.5" /> Nouveau bon</Button>
      {rows.length === 0 ? <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">Aucun bon.</div> :
        <div className="space-y-2">{rows.map((r) => (
          <div key={r.id} data-testid={`${kind}-row-${r.id}`} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
            <div className="flex-1"><div className="text-sm font-medium text-slate-800">{r.number} · {r.client_name || "—"}</div><div className="text-xs text-slate-400">{r.date} {r.work ? `· ${r.work}` : ""}</div></div>
            <Button size="sm" variant="outline" onClick={() => print(r)} data-testid={`${kind}-print-${r.id}`} className="border-slate-200"><Printer className="w-3.5 h-3.5 mr-1" /> Imprimer</Button>
            <button onClick={() => del(r.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-red-600"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}</div>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nouveau bon</DialogTitle><DialogDescription className="sr-only">Création d'un bon</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Client</Label>
              <Select value={form.client_id || ""} onValueChange={(v) => setForm({ ...form, client_id: v })}><SelectTrigger data-testid={`${kind}-client`} className="bg-slate-50 border-slate-200"><SelectValue placeholder="Client" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.company}</SelectItem>)}</SelectContent></Select></div>
            {kind === "bi" && <>
              <Input placeholder="Adresse" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-slate-50 border-slate-200" />
              <Input placeholder="Travaux réalisés" value={form.work || ""} onChange={(e) => setForm({ ...form, work: e.target.value })} data-testid="bi-work" className="bg-slate-50 border-slate-200" />
              <Input type="number" placeholder="Temps passé (h)" value={form.hours || ""} onChange={(e) => setForm({ ...form, hours: Number(e.target.value) })} className="bg-slate-50 border-slate-200" />
            </>}
            <Textarea placeholder={kind === "bi" ? "Détails / observations" : "Éléments livrés / observations"} value={form.details || form.observations || ""} onChange={(e) => setForm({ ...form, [kind === "bi" ? "details" : "observations"]: e.target.value })} className="bg-slate-50 border-slate-200" />
            <Input placeholder="Signature client (nom)" value={form.client_signature || ""} onChange={(e) => setForm({ ...form, client_signature: e.target.value })} className="bg-slate-50 border-slate-200" />
          </div>
          <DialogFooter><Button onClick={save} data-testid={`btn-save-${kind}`} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
